import type { Agent, AgentFavoriteRequest, AgentLikeToggleRequest, AgentListParams, DifyAppInfo, DifyAppParameters, DifyUserInputFormItem, DifyWorkflowRunData, DifyWorkflowRunRequest, GetSsoTicketParams, RSsoIframeUrlVo } from './types';
import {
  buildWorkbenchListResponse,
  logWorkbenchRequest,
  mapLikeToggleData,
  normalizeAgentActionResponse,
} from './workbench';
import { parseAgentRatingFromPayload } from '@/constants/agentModule';
import request from '@/utils/request';

function tryParseJson(text: string): any {
  try {
    return JSON.parse(text);
  }
  catch {
    return null;
  }
}

function parseSseTextForWorkflowFinished(sseText: string): any {
  const chunks = sseText
    .split(/\n{2,}/)
    .map(chunk => chunk.trim())
    .filter(Boolean);

  let latestWorkflowFinishedData: any = null;
  for (const chunk of chunks) {
    const lines = chunk.split('\n').map(line => line.trim());
    let eventName = '';
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      }
      else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }
    if (!dataLines.length)
      continue;
    const mergedData = dataLines.join('\n');
    const parsed = tryParseJson(mergedData);
    if (!parsed)
      continue;
    const parsedEvent = String(parsed?.event ?? eventName ?? '').trim();
    if (parsedEvent === 'workflow_finished')
      latestWorkflowFinishedData = parsed?.data ?? parsed;
  }

  return latestWorkflowFinishedData;
}

function normalizeWorkflowFiles(rawData: any): DifyWorkflowRunData['files'] {
  const directFiles = Array.isArray(rawData?.files) ? rawData.files : [];
  const outputText = rawData?.outputs?.text;
  const outputTextFiles = Array.isArray(outputText)
    ? outputText.filter((item: any) => item && typeof item === 'object' && String(item?.dify_model_identity || '').includes('__dify__file__'))
    : [];
  const source = directFiles.length ? directFiles : outputTextFiles;

  if (!source.length)
    return [];

  return source.map((item: any) => ({
    ...(item || {}),
    id: item?.id ?? item?.related_id,
    related_id: item?.related_id,
    filename: item?.filename ?? item?.name,
    size: Number(item?.size ?? item?.bytes ?? 0) || undefined,
    url: item?.url ?? item?.remote_url,
  }));
}

function parseWorkflowRunRawData(payload: any): any {
  if (payload && typeof payload === 'object')
    return payload;
  if (typeof payload !== 'string')
    return {};

  const trimmed = payload.trim();
  if (!trimmed)
    return {};

  const directParsed = tryParseJson(trimmed);
  if (directParsed && typeof directParsed === 'object')
    return directParsed;

  const workflowFinishedData = parseSseTextForWorkflowFinished(trimmed);
  if (workflowFinishedData && typeof workflowFinishedData === 'object')
    return workflowFinishedData;

  return {};
}

function extractWorkflowTextContent(raw: any): string {
  const directCandidates = [
    raw?.content,
    raw?.answer,
    raw?.text,
    raw?.output_text,
    raw?.result,
  ];
  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim())
      return candidate;
  }

  const outputsText = raw?.outputs?.text;
  if (typeof outputsText === 'string' && outputsText.trim())
    return outputsText;
  if (Array.isArray(outputsText)) {
    const chunks = outputsText
      .map((item: any) => {
        if (typeof item === 'string')
          return item.trim();
        if (item && typeof item === 'object') {
          const text = item.text ?? item.content ?? item.value ?? item.output_text;
          return typeof text === 'string' ? text.trim() : '';
        }
        return '';
      })
      .filter(Boolean);
    if (chunks.length)
      return chunks.join('\n');
  }
  return '';
}

// 智能体相关API
export const agentApi = {
  // 获取智能体列表
  getAgentList: (params?: AgentListParams) => {
    const query: AgentListParams = {
      expertTab: 'all',
      ...params,
    };
    // 调试：打印请求参数
    console.log('[agentApi.getAgentList] params =', query);
    // 以 query 方式透传原始参数（保持 userId 字符串不被转型）
    return request.get('/system/agent/list', query).then((res: any) => {
      console.log('[agentApi.getAgentList] /system/agent/list response =', res);
      // 后端返回示例：{ total, rows: [...], code, msg }
      const rows = Array.isArray(res?.rows) ? res.rows : [];
      const mapped: Agent[] = rows.map((it: any) => ({
        id: String(it.id ?? it.externalAgentId ?? ''),
        rawId: String(it.id ?? it.externalAgentId ?? ''), // 保存原始ID字符串
        numericId: (() => {
          const rawId = it.id ?? it.externalAgentId ?? 0;
          // 对于大整数，保持字符串格式，避免精度丢失
          if (typeof rawId === 'string') {
            // 检查是否为有效的数字字符串
            if (/^\d+$/.test(rawId)) {
              // 对于超出安全整数范围的大整数，返回字符串形式的数字
              // 这样在需要数字类型时可以通过 Number() 转换，但保持原始精度
              return Number(rawId);
            }
            return 0;
          }
          // 如果已经是数字，直接返回
          return Number(rawId) || 0;
        })(),
        name: it.agentNickname ?? '未命名智能体',
        description: it.agentDescription ?? it.localizedDescription ?? '',
        icon: it.agentIcon ?? 'User',
        color: '#10A6E2',
        version: it.version ?? it.agentVersion ?? 'v1.0.0',
        // 智能体类型标签统一使用后端的 category 字段（chat_model.category）
        category: it.category ?? '默认分类',
        modelCategory: it.category != null ? String(it.category) : undefined,
        taskCapability: (() => {
          const v = String(it.taskCapability ?? it.supportScheduledTask ?? '0');
          // 统一返回 "0"/"1"
          if (v === 'true')
            return '1';
          if (v === 'false')
            return '0';
          return v === '1' ? '1' : '0';
        })(),
        tagNames: it.tagNames as string[] | undefined,
        agentSource: it.agentSource as 'internal' | 'external' | 'third_party' | undefined,
        agentCover: it.agentCover as string | undefined,
        thirdPartyUrl: it.thirdPartyUrl as string | undefined,
        status: ((): 'available' | 'disabled' | 'offline' => {
          const s = String(it.agentStatus ?? '').toLowerCase();
          // 0:可用 1:禁用 2:下线
          if (s === '0' || s === 'available' || s === 'online')
            return 'available';
          if (s === '1' || s === 'disabled')
            return 'disabled';
          return 'offline';
        })(),
        instantChatCount: Number(it.usageCount ?? it.viewCount ?? 0),
        taskExecutionCount: Number(it.likeCount ?? 0),
        rating: parseAgentRatingFromPayload(it),
      }));

      return {
        code: res?.code ?? 200,
        message: res?.msg ?? '操作成功',
        data: {
          list: mapped,
          total: Number(res?.total ?? mapped.length),
          page: 1,
          pageSize: mapped.length,
        },
        success: true,
        timestamp: Date.now(),
      };
    });
  },

  /** 专家工作台卡片列表（/system/agent/workbench/list） */
  getWorkbenchAgentList: (params?: AgentListParams) => {
    const query: AgentListParams = {
      expertTab: 'all',
      keyword: '',
      ...params,
    };
    logWorkbenchRequest('[agentApi.getWorkbenchAgentList] params =', query);
    return request.get('/system/agent/workbench/list', query).then((res: any) => {
      logWorkbenchRequest('[agentApi.getWorkbenchAgentList] response =', res);
      return buildWorkbenchListResponse(res);
    });
  },

  /** Dify 应用基本信息（智能体 id 即路由/列表中的 agent id） */
  getDifyAppInfo: (id: string) => {
    return request.get(`/system/agent/dify/app-info/${id}`).then((res: any) => {
      const raw = res?.data;
      const mapped: DifyAppInfo | null = raw
        ? {
            name: String(raw.name ?? ''),
            description: String(raw.description ?? ''),
            tags: Array.isArray(raw.tags) ? raw.tags.map((t: any) => String(t)) : [],
            mode: String(raw.mode ?? ''),
            authorName: String(raw.author_name ?? ''),
          }
        : null;
      return {
        code: res?.code ?? 200,
        message: res?.msg ?? '操作成功',
        data: mapped,
        success: Number(res?.code ?? 200) === 200,
        timestamp: Date.now(),
      };
    });
  },

  /** Dify 应用参数（含 user_input_form；id 为智能体 id） */
  getDifyAppParameters: (id: string) => {
    return request.get(`/system/agent/dify/app-parameters/${id}`).then((res: any) => {
      const raw = res?.data;
      const user_input_form: DifyUserInputFormItem[] = Array.isArray(raw?.user_input_form)
        ? raw.user_input_form
        : [];
      const mapped: DifyAppParameters | null = raw && typeof raw === 'object'
        ? { ...raw, user_input_form }
        : null;
      return {
        code: res?.code ?? 200,
        message: res?.msg ?? '操作成功',
        data: mapped,
        success: Number(res?.code ?? 200) === 200,
        timestamp: Date.now(),
      };
    });
  },

  /** Dify workflow 执行（id 为智能体 id，请求体保持不变） */
  runDifyWorkflow: (id: string, body: DifyWorkflowRunRequest) => {
    return request.post(`/system/agent/dify/workflow/run/${id}/stream`, body).then((res: any) => {
      const raw = parseWorkflowRunRawData(res?.data);
      const files = normalizeWorkflowFiles(raw);
      const normalizedContent = extractWorkflowTextContent(raw);
      const mapped: DifyWorkflowRunData = {
        ...(raw as Record<string, unknown>),
        content: normalizedContent,
        files,
      };
      if (import.meta.env.DEV) {
        console.log('[DifyWorkflow] /system/agent/dify/workflow/run/{id}/stream 响应数据:', {
          id,
          code: res?.code,
          message: res?.msg,
          rawResponse: res,
          parsedRawData: raw,
          mappedData: mapped,
        });
      }
      return {
        code: res?.code ?? 200,
        message: res?.msg ?? '操作成功',
        data: mapped,
        success: Number(res?.code ?? 200) === 200,
        timestamp: Date.now(),
      };
    });
  },

  /** 将 Dify 文件相对 URL 转换为可下载地址（id 为智能体 id） */
  resolveDifyFileDownloadUrl: async (id: string, url: string) => {
    const path = `/system/agent/dify/file/download/${id}`;
    const remoteBase = import.meta.env.VITE_API_URL || import.meta.env.VITE_WEB_BASE_API || '';
    const endpoint = remoteBase
      ? `${String(remoteBase).replace(/\/$/, '')}/${path.replace(/^\//, '')}`
      : path;

    const { useUserStore } = await import('@/stores');
    const userStore = useUserStore();
    const token = userStore.token;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ url }),
    });

    const contentType = response.headers.get('content-type') || '';
    console.log('[DifyFileDownload] /system/agent/dify/file/download/{id} 响应基础信息:', {
      id,
      endpoint,
      requestBody: { url },
      status: response.status,
      ok: response.ok,
      contentType,
    });
    if (contentType.includes('application/json')) {
      const json = await response.json();
      console.log('[DifyFileDownload] JSON 响应数据:', {
        id,
        requestUrl: url,
        responseJson: json,
      });
      const raw = json?.data;
      const parsedUrl = typeof raw === 'string'
        ? raw
        : String(
            raw?.url
            ?? raw?.downloadUrl
            ?? raw?.download_url
            ?? raw?.data?.url
            ?? '',
          ).trim();
      return {
        code: json?.code ?? (response.ok ? 200 : response.status),
        message: json?.msg ?? json?.message ?? (response.ok ? '操作成功' : '请求失败'),
        data: {
          ...(raw && typeof raw === 'object' ? raw : {}),
          url: parsedUrl || undefined,
        },
        success: response.ok && (Number(json?.code ?? 200) === 200 || Number(json?.code ?? 200) === 0),
        timestamp: Date.now(),
      };
    }

    // 接口直接返回文件流（如 docx/xlsx），转为 blob URL 供前端附件链接展示
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    console.log('[DifyFileDownload] Blob 响应数据:', {
      id,
      requestUrl: url,
      blobType: blob.type,
      blobSize: blob.size,
      blobUrl,
    });
    return {
      code: response.ok ? 200 : response.status,
      message: response.ok ? '操作成功' : '请求失败',
      data: {
        url: blobUrl,
      },
      success: response.ok,
      timestamp: Date.now(),
    };
  },

  // 获取第三方智能体 SSO 一次性票据并返回 iframe URL
  getSsoTicket: (params: GetSsoTicketParams) => {
    console.log('[SSO] /api/v1/sso/get-ticket request params:', params);
    return request.get('/api/v1/sso/get-ticket', params).then((res: RSsoIframeUrlVo) => {
      console.log('[SSO] /api/v1/sso/get-ticket response:', res);
      return {
        code: res?.code ?? 200,
        message: res?.msg ?? '操作成功',
        data: res?.data,
        success: Number(res?.code ?? 200) === 200,
        timestamp: Date.now(),
      };
    });
  },

  // 根据ID获取智能体详情
  getAgentById: (id: string) => {
    return request.get(`/system/agent/user/${id}`).then((res: any) => {
      // 兼容包裹结构 { code, msg, data }
      const agent = res?.data ?? res;
      const mapped: Agent = {
        id: String(agent.id ?? agent.externalAgentId ?? ''),
        numericId: (() => {
          const rawId = agent.id ?? agent.externalAgentId ?? 0;
          // 检查是否为安全整数，避免精度丢失
          if (Number.isSafeInteger(Number(rawId))) {
            return Number(rawId);
          }
          // 对于大整数，尝试解析为数字，如果失败则使用0
          const numId = Number(rawId);
          return Number.isNaN(numId) ? 0 : numId;
        })(),
        name: agent.agentNickname ?? '未命名智能体',
        description: agent.agentDescription ?? agent.localizedDescription ?? '',
        icon: agent.agentIcon ?? 'User',
        color: '#10A6E2',
        version: agent.version ?? agent.agentVersion ?? 'v1.0.0',
        // 智能体类型标签统一使用后端的 category 字段，不再依赖 agentCategory
        category: agent.category ?? '默认分类',
        modelCategory: agent.category != null ? String(agent.category) : undefined,
        taskCapability: (() => {
          const v = String(agent.taskCapability ?? agent.supportScheduledTask ?? '0');
          if (v === 'true')
            return '1';
          if (v === 'false')
            return '0';
          return v === '1' ? '1' : '0';
        })(),
        tagNames: agent.tagNames as string[] | undefined,
        agentSource: agent.agentSource as 'internal' | 'external' | 'third_party' | undefined,
        agentCover: agent.agentCover as string | undefined,
        thirdPartyUrl: agent.thirdPartyUrl as string | undefined,
        status: ((): 'available' | 'disabled' | 'offline' => {
          const s = String(agent.agentStatus ?? '').toLowerCase();
          // 0:可用 1:禁用 2:下线
          if (s === '0' || s === 'available' || s === 'online')
            return 'available';
          if (s === '1' || s === 'disabled')
            return 'disabled';
          return 'offline';
        })(),
        instantChatCount: Number(agent.usageCount ?? agent.viewCount ?? 0),
        taskExecutionCount: Number(agent.likeCount ?? 0),
        rating: parseAgentRatingFromPayload(agent),
        personalConversationCount: Number(agent.personalConversationCount ?? 0),
        personalTaskExecutionCount: Number(agent.personalTaskExecutionCount ?? 0),
      };

      return {
        code: res?.code ?? 200,
        message: res?.msg ?? '操作成功',
        data: mapped,
        success: true,
        timestamp: Date.now(),
      };
    });
  },

  // 置顶智能体
  pinAgent: (agentId: string | number) => {
    return request.put(`/system/agent/pin/${agentId}`).then((res: any) => ({
      code: res?.code ?? 200,
      msg: res?.msg ?? '操作成功',
      data: res?.data,
    }));
  },

  /** 收藏 / 取消收藏智能体 */
  setAgentFavorite: (agentId: string | number, favorite: boolean) => {
    const body: AgentFavoriteRequest = { favorite };
    logWorkbenchRequest('[agentApi.setAgentFavorite] params =', { agentId, body });
    return request.put(`/system/agent/favorite/${agentId}`, body).then((res: any) => {
      logWorkbenchRequest('[agentApi.setAgentFavorite] response =', res);
      return normalizeAgentActionResponse(res);
    });
  },

  /** 点赞 / 取消点赞智能体（用户端） */
  toggleAgentLike: (agentId: string | number, liked: boolean) => {
    const body: AgentLikeToggleRequest = { liked };
    logWorkbenchRequest('[agentApi.toggleAgentLike] params =', { agentId, body });
    return request.put(`/system/agent/like/toggle/${agentId}`, body).then((res: any) => {
      logWorkbenchRequest('[agentApi.toggleAgentLike] response =', res);
      const payload = (res?.data && typeof res.data === 'object') ? res.data as Record<string, unknown> : {};
      return normalizeAgentActionResponse(res, mapLikeToggleData(payload, liked));
    });
  },

  /** 开始对话 — 增加使用次数 */
  incrementAgentUsage: (id: string | number) => {
    logWorkbenchRequest('[agentApi.incrementAgentUsage] params =', { id });
    return request.put(`/system/agent/usage/${id}`).then((res: any) => {
      logWorkbenchRequest('[agentApi.incrementAgentUsage] response =', res);
      return normalizeAgentActionResponse(res);
    });
  },

  // 创建智能体
  createAgent: (data: any) => {
    return request.post('/api/agents', data);
  },

  // 更新智能体
  updateAgent: (id: string, data: any) => {
    return request.put(`/api/agents/${id}`, data);
  },

  // 删除智能体
  deleteAgent: (id: string) => {
    return request.delete(`/api/agents/${id}`);
  },
};
